import React from 'react';
import { isEmpty } from 'lodash-es';
import { renderToString } from 'react-dom/server';
import { number_with_delimiter as numberWithDelimiter } from 'number_helpers';

import defaultLineChartConfig from '../../../../components/Chart/LineChart';

import { decorateTooltipForecastLabel } from './forecasts';
import { buildRects, buildVerticalDashedLine, buildHorizontalLines } from '../../../../utils/charts/renderers';
import helper, { numberToHumanSummary } from '../../../../utils/formatter';

export const seriesOrder = ['forecast', 'consensus'];

export const colorBySeries = {
  forecast: '#348BE3',
  consensus: '#848EA1',
};

export const nameBySeries = {
  forecast: 'Apptopia Forecast',
  consensus: 'Consensus',
};

const metadataNameMapping = {
  [undefined]: 'Consensus',
  [null]: 'Consensus',
  preq: 'PreQ Consensus',
  current: 'Consensus',
  actuals: 'Actual',
};

export const setupChartConfig = () => {
  const renderRects = buildRects();
  const renderVerticalDashedLine = buildVerticalDashedLine();
  let customLinesList = [];

  let plotBandWithCoord;
  return ({
    view,
    height,
    metadata = [],
    publishedDates,
    activeCategory,
    isScreenshot = false,
  }) => defaultLineChartConfig({
    chartOptions: {
      type: 'line',
      height,
      spacing: isScreenshot ? [10, 36, 4, 0] : [10, 36, 4, 0],
      style: {
        overflow: 'visible',
      },
      events: {
        load() {
          // Reset coords
          plotBandWithCoord = undefined;
        },
        render(event) {
          const { chartHeight, spacing, xAxis } = event.target;
          customLinesList = document.querySelectorAll('.highcharts-tracker-line-custom');

          if (xAxis[0]) {
            const firstPlotBands = xAxis[0].plotLinesAndBands[0];

            if (firstPlotBands) {
              const topOffset = spacing[0] || 0;
              const plotBandsWidth = Math.round(firstPlotBands.axis.translationSlope);

              if (plotBandsWidth !== plotBandWithCoord) {
                plotBandWithCoord = plotBandsWidth;

                renderRects(this.renderer, [{
                  height: chartHeight - topOffset,
                  width: plotBandsWidth,
                  top: topOffset,
                  left: 0,
                }]);

                renderVerticalDashedLine(this.renderer, {
                  topLeft: plotBandsWidth,
                  topRight: topOffset,
                  bottomLeft: plotBandsWidth,
                  bottomRight: chartHeight,
                });
              }
            }
          }

          const labelPoints = [];
          this.series?.forEach((seria) => seria.points?.forEach((point) => {
            if (!isEmpty(point.dataLabel)) {
              labelPoints.push(point);
              const connectorLength = this.chartWidth - (point.dataLabel.anchorX + point.dataLabel.width)
              const lineParams = {
                top: point.plotY + 10,
                left: point.plotX,
                width: connectorLength < 0 ? 0 : connectorLength,
                stroke: point?.color,
                fill: point?.color,
                class: 'highcharts-tracker-line-custom',
              };
              const strokeParams = {
                topLeft: point.plotX,
                topRight: point.plotY + 10,
                bottomLeft: this.chartWidth - point.dataLabel.width,
                bottomRight: point.plotY + 10,
                stroke: point?.color,
                'stroke-width': 1,
                dashstyle: 'ShortDash',
                'stroke-dasharray': '2, 2',
                zIndex: 1500,
                class: 'highcharts-tracker-line-custom',
              };
              customLinesList.forEach(l => l.remove());
              const isConsensus = point.series.name.toLowerCase().includes('consensus');
              if (!isConsensus) {
                const renderHorizontalDashedLine = buildVerticalDashedLine(strokeParams);
                renderHorizontalDashedLine(this.renderer, strokeParams);
              } else {
                const renderHorizontalLines = buildHorizontalLines(lineParams);
                this.renderer.cache = {};
                renderHorizontalLines(this.renderer, [lineParams]);
              }

              setTimeout(() => {
                const diff = labelPoints[0].plotY - labelPoints[1].plotY;
                const higherValue = Math.max(labelPoints[0].plotY, labelPoints[1].plotY);
                if (diff !== 0 && Math.abs(diff) < 24) {
                  point?.dataLabel?.attr({
                    y: point.plotY + (higherValue > point?.plotY ? -19 : -1),
                  });
                } else if (diff === 0) {
                  point?.dataLabel?.attr({
                    y: point.plotY + (isConsensus ? -1 : -19),
                  });
                }
              });
            }
          }));
        },
      },
    },
    xAxisOptions: {
      type: 'category',
      labels: {
        autoRotation: null,
        formatter() {
          return this.value === activeCategory ? `<b>${this.value}</b>` : this.value;
        },
        style: {
          color: '#363D54',
          fontSize: 11,
        },
      },
      plotBands: [{
        color: '#F8F9FB',
        from: -0.5,
        to: 0.5,
      }],
    },
    yAxisOptions: {
      opposite: true,
      gridLineColor: '#E1E5EB',
      gridLineWidth: 1,
      startOnTick: true,
      endOnTick: true,
    },
    yAxisLabels: {
      align: 'right',
      style: {
        color: '#363D54',
        fontSize: 11,
      },
      x: 30,
      formatter() {
        if (view === 'change') {
          return `${numberToHumanSummary(this.value)}%`;
        }

        return numberWithDelimiter(this.value);
      },
    },
    tooltipOptions: {
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 0,
      padding: 0,
      shared: true,
      outside: true,
      shadow: false,
      style: {
        color: '#363D54',
        fontSize: 12,
      },
      useHTML: true,
      formatter() {
        let rows;
        if (Array.isArray(this.points)) {
          // Rows
          rows = this.points.map((point) => {
            const formattedValue = numberWithDelimiter(Math.round(point.y));
            const consensusName = point.series.name === 'Consensus' ? metadataNameMapping[metadata[point.point.index]] : 'Consensus';
            return renderToString((
              <tr>
                <td className="v-a-m text-left text-12 p-t-xxxs p-r-mdl p-b-0 p-l-0">
                  <span className="l-h-md">
                    {point.series.name === 'Apptopia Forecast' ? <b>Apptopia Forecast</b> : consensusName}
                  </span>
                </td>
                <td className="v-a-m text-right text-12 p-t-xxxs p-r-0 p-b-0 p-l-0">
                  {view === 'change' ? (
                    <span className="l-h-md">
                      {Math.round(point.y)}% <span className="text-bold" style={{ fontSize: 9 }}>YoY</span>
                    </span>
                  ) : (
                    <span className="l-h-md">
                      {formattedValue}
                    </span>
                  )}
                </td>
              </tr>
            ));
          });
        } else return false;

        return `
          <div class="series-tooltip bg-white b-a b-rds-8" style="padding: 8px 16px;box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.16);">
            <div class="d-flex justify-content-between l-h-md">
              <b>${decorateTooltipForecastLabel(this.x)}</b>
              ${publishedDates[this.x] ? `<span>${publishedDates[this.x]}</span>` : ''}
            </div>
            <table class="table">${rows.join('')}</table>
          </div>
        `;
      },
    },
    plotOptionsSeries: {
      animation: !isScreenshot,
      linecap: 'round',
      marker: {
        enabled: true,
        symbol: 'circle',
        radius: 3,
        lineWidth: 1,
        lineColor: null,
        fillColor: '#FFFFFF',
      },
      dataLabels: {
        enabled: false,
        shape: 'callout',
        zIndex: 1500,
        style: {
          color: '#fff',
          fontSize: '12px',
        },
        verticalAlign: 'middle',
        crop: false,
        overflow: 'allow',
        useHTML: true,
        borderRadius: 0,
        padding: 0,
        allowOverlap: true,
        formatter() {
          const { point } = this;
          const isConsensus = point.series.name.toLowerCase().includes('consensus');
          setTimeout(() => {
            point?.dataLabel?.attr({
              x: this?.series?.chart?.chartWidth - point.dataLabel.width - (isScreenshot && isConsensus ? 2 : 0),
            });
          });
          return `
            <div
              class="d-flex justify-content-between"
              style="gap: 2px;
                font-family: Lato;
                font-size: 11px;
                font-weight: 700;
                text-transform: uppercase;
                line-height: 165%"
            >
              <div style="background-color: ${point.color}; padding: 0 6px;">
                ${isConsensus ? 'CONSENSUS' : 'FORECAST'}
              </div>
              <div style="background-color: ${point.color}; padding: 0 6px; ">
                ${helper.numberWithCommas(point.y)}${(view === 'change') ? '%' : ''}
              </div>
            </div>`;
        },
      },
    },
  });
};
